import { CornerDownLeft, Search } from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { useEscapeKey } from '@/hooks/useDismiss'
import { useFocusTrap, useScrollLock } from '@/hooks/useFocusTrap'
import { useT } from '@/i18n'
import { VISIBLE_MODULES, type AdminModule } from '@/config/modules'
import { cn, fuzzyMatch, fuzzyScore } from '@/lib/utils'

type Candidate = {
  module: AdminModule
  title: string
  group: string
  /** Indices into `title` that matched, for highlighting. */
  highlight: number[]
  score: number
}

export type CommandPaletteProps = {
  open: boolean
  onClose: () => void
}

/**
 * ⌘K / Ctrl+K jump-to. Hand-built so the keyboard model is exactly right:
 * ArrowUp/Down move the active option, Home/End jump, Enter navigates, Escape
 * closes, and the listbox is wired with `aria-activedescendant` so the active
 * option is announced without stealing focus from the input.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps): ReactNode {
  const t = useT()
  const navigate = useNavigate()
  const panelRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  useEscapeKey(open, onClose)
  useFocusTrap(panelRef, open)
  useScrollLock(open)

  useEffect(() => {
    if (open) {
      setQuery('')
      setActiveIndex(0)
    }
  }, [open])

  const results = useMemo<Candidate[]>(() => {
    const candidates: Candidate[] = []

    for (const module of VISIBLE_MODULES) {
      const title = t(`modules.${module.id}.title`)
      const group = t(`nav.groups.${module.group}`)
      const searchable = [title, group, module.code, ...module.keywords]

      if (query.trim() === '') {
        candidates.push({ module, title, group, highlight: [], score: 0 })
        continue
      }

      let best: { highlight: number[]; score: number } | null = null
      for (const [index, field] of searchable.entries()) {
        const matched = fuzzyMatch(field, query.trim())
        if (!matched) continue
        // Penalise matches that only hit a hidden keyword so the visible label wins.
        const score = fuzzyScore(field, matched) + (index === 0 ? 0 : 40)
        if (!best || score < best.score) {
          best = { highlight: index === 0 ? matched : [], score }
        }
      }

      if (best) candidates.push({ module, title, group, ...best })
    }

    return query.trim() === '' ? candidates : candidates.sort((a, b) => a.score - b.score)
  }, [query, t])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Keep the active option scrolled into view while arrowing through a long list.
  useEffect(() => {
    if (!open) return
    listRef.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, open])

  if (!open) return null

  const activeCandidate = results[activeIndex]

  const select = (candidate: Candidate | undefined): void => {
    if (!candidate) return
    navigate(candidate.module.path)
    onClose()
  }

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (results.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => (index + 1) % results.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => (index - 1 + results.length) % results.length)
    } else if (event.key === 'Home') {
      event.preventDefault()
      setActiveIndex(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      setActiveIndex(results.length - 1)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      select(results[activeIndex])
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[10vh]">
      <div
        className="animate-overlay-in absolute inset-0 bg-neutral-950/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.title')}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="animate-panel-in relative flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-3 outline-none"
      >
        <div className="flex items-center gap-2.5 border-b border-border px-3.5">
          <Search aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          <input
            data-autofocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('palette.placeholder')}
            aria-label={t('palette.title')}
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-list"
            aria-activedescendant={
              activeCandidate ? `command-option-${activeCandidate.module.id}` : undefined
            }
            autoComplete="off"
            spellCheck={false}
            className="h-12 w-full bg-transparent text-body text-foreground outline-none placeholder:text-muted-foreground"
          />
          <kbd className="hidden shrink-0 rounded-sm border border-border px-1.5 py-0.5 text-overline text-muted-foreground sm:block">
            ESC
          </kbd>
        </div>

        {results.length === 0 ? (
          <p className="px-4 py-10 text-center text-body-sm text-muted-foreground">
            {t('palette.empty', { query })}
          </p>
        ) : (
          <ul
            ref={listRef}
            id="command-palette-list"
            role="listbox"
            aria-label={t('palette.resultsLabel', { count: results.length })}
            className="scrollbar-thin min-h-0 flex-1 overflow-y-auto p-1.5"
          >
            {results.map((candidate, index) => {
              const Icon = candidate.module.icon
              const active = index === activeIndex
              return (
                <li key={candidate.module.id}>
                  <button
                    type="button"
                    id={`command-option-${candidate.module.id}`}
                    role="option"
                    aria-selected={active}
                    data-active={active}
                    tabIndex={-1}
                    onPointerMove={() => setActiveIndex(index)}
                    onClick={() => select(candidate)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left',
                      'transition-colors duration-hover',
                      active ? 'bg-muted text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    <Icon aria-hidden="true" className="size-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate text-body-sm text-foreground">
                      <HighlightedText text={candidate.title} indices={candidate.highlight} />
                    </span>
                    <span className="shrink-0 text-caption text-muted-foreground">
                      {candidate.group}
                    </span>
                    {active ? (
                      <CornerDownLeft aria-hidden="true" className="size-3.5 shrink-0" />
                    ) : null}
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        <div className="flex items-center gap-4 border-t border-border px-3.5 py-2 text-caption text-muted-foreground">
          <span>↑↓ {t('palette.hintNavigate')}</span>
          <span>↵ {t('palette.hintSelect')}</span>
          <span>esc {t('palette.hintClose')}</span>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function HighlightedText({ text, indices }: { text: string; indices: number[] }): ReactNode {
  if (indices.length === 0) return text
  const set = new Set(indices)
  return (
    <>
      {Array.from(text).map((character, index) =>
        set.has(index) ? (
          <mark key={index} className="bg-transparent font-semibold text-info">
            {character}
          </mark>
        ) : (
          <span key={index}>{character}</span>
        ),
      )}
    </>
  )
}
